/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ----------------------------------------------------------------------------
 *   AdvisorPanel (Spec 256 — Backup Cost Governance, US5 / T050)
 *   The cost-optimization advisor surface: a config-alignment score + prioritized,
 *   directional (never-currency) suggestions to move the fleet toward the best-case
 *   configuration. Advisory only — nothing here mutates config. A near-best-case
 *   fleet shows a clean "already optimal" state (SC-005).
 * ============================================================================
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { TrendingDown, CheckCircle2 } from 'lucide-react';
import { DashboardPanel, Badge, LoadingState, ErrorState, EmptyState } from '@/components/ui';
import { costApi, type CostSuggestion, type SuggestionPriority } from '../../api/cost';

const PRIORITY_VARIANT: Record<SuggestionPriority, 'danger' | 'warning' | 'info'> = {
  high: 'danger',
  medium: 'warning',
  low: 'info',
};
const PRIORITY_RANK: Record<SuggestionPriority, number> = { high: 0, medium: 1, low: 2 };

const SuggestionCard: React.FC<{ s: CostSuggestion }> = ({ s }) => (
  <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
    <div className="mb-2 flex items-center gap-2">
      <TrendingDown className="h-4 w-4 text-[var(--color-accent)]" />
      <Badge variant={PRIORITY_VARIANT[s.priority]}>{s.priority}</Badge>
      <span className="text-[10px] uppercase tracking-wide text-[var(--color-text-tertiary)]">{s.category}</span>
    </div>
    <dl className="space-y-1.5 text-sm">
      <div className="flex gap-2">
        <dt className="w-16 shrink-0 text-[var(--color-text-tertiary)]">Now</dt>
        <dd className="text-[var(--color-text-secondary)]">{s.current}</dd>
      </div>
      <div className="flex gap-2">
        <dt className="w-16 shrink-0 text-[var(--color-text-tertiary)]">Change</dt>
        <dd className="text-[var(--color-text-primary)]">{s.change}</dd>
      </div>
      <div className="flex gap-2">
        <dt className="w-16 shrink-0 text-[var(--color-text-tertiary)]">Saves</dt>
        <dd className="text-[var(--color-text-secondary)]">{s.benefit}</dd>
      </div>
    </dl>
  </div>
);

export const AdvisorPanel: React.FC = () => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['backup-cost-advisor'],
    queryFn: () => costApi.advisor(),
  });

  if (isLoading) return <LoadingState rows={4} />;
  if (isError || !data) {
    return <ErrorState title="Could not load advisor" message="Please try again." onRetry={() => refetch()} />;
  }

  const { configScore, suggestions } = data;
  const ordered = [...suggestions].sort((a, b) => PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]);

  return (
    <DashboardPanel
      title="Cost-optimization advisor"
      subtitle="Environment-specific suggestions to move toward the best-case backup configuration. Advisory only — nothing here changes your config. Directional, never dollar figures."
      titleInfo={`Config alignment: ${configScore.current}% of ${configScore.bestCasePct}% best-case (share of plans already on the cheapest server-side in-region route).`}
    >
      {ordered.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 className="h-8 w-8 text-[var(--color-status-success)]" />}
          title="Already close to best-case"
          message="No cost-saving changes stand out from the current configuration and routes."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {ordered.map((s, i) => (
            <SuggestionCard key={`${s.category}-${i}`} s={s} />
          ))}
        </div>
      )}
    </DashboardPanel>
  );
};

export default AdvisorPanel;
